import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Building, Upload, User, CheckCircle } from 'lucide-react';
import { authAPI, pdfAPI } from '../services/api';
import { OnboardingData, LoginResponse } from '../types';

interface OnboardingProps {
  onComplete: (session?: LoginResponse) => void;
  onBack: () => void;
}

const Onboarding: React.FC<OnboardingProps> = ({ onComplete, onBack }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState<OnboardingData>({
    hotel: {
      name: '',
      address: '',
      phone: '',
      email: '',
      description: '',
    },
    admin: {
      name: '',
      email: '',
      password: '',
    },
  });

  const [pdfs, setPdfs] = useState<{
    hotel_info?: File;
    room_service?: File;
    additional_files: File[];
  }>({
    additional_files: [],
  });

  const steps = [
    { id: 1, title: 'Hotel Information', icon: Building },
    { id: 2, title: 'Admin Account', icon: User },
    { id: 3, title: 'Upload Documents', icon: Upload },
    { id: 4, title: 'Complete Setup', icon: CheckCircle },
  ];

  const handleInputChange = (section: 'hotel' | 'admin', field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleFileChange = (category: string, file: File | null) => {
    if (category === 'additional_files' && file) {
      setPdfs(prev => ({
        ...prev,
        additional_files: [...prev.additional_files, file],
      }));
    } else if (file) {
      setPdfs(prev => ({
        ...prev,
        [category]: file,
      }));
    }
  };

  const removeAdditionalFile = (index: number) => {
    setPdfs(prev => ({
      ...prev,
      additional_files: prev.additional_files.filter((_, i) => i !== index),
    }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.hotel.name && formData.hotel.email);
      case 2:
        return !!(formData.admin.name && formData.admin.email && formData.admin.password);
      case 3:
        return true; // PDFs are optional
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      setError('');
    } else {
      setError('Please fill in all required fields');
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const handleComplete = async () => {
    setLoading(true);
    setError('');

    try {
      let onboardRes: Awaited<ReturnType<typeof authAPI.onboard>>;
      onboardRes = await authAPI.onboard(formData);
      if (onboardRes.token && onboardRes.user && onboardRes.hotel) {
        localStorage.setItem('authToken', onboardRes.token);
        localStorage.setItem('user', JSON.stringify(onboardRes.user));
        localStorage.setItem('hotel', JSON.stringify(onboardRes.hotel));
      }

      const uploadPromises: Promise<any>[] = [];
      
      if (pdfs.hotel_info) {
        uploadPromises.push(pdfAPI.uploadPdf(pdfs.hotel_info, 'hotel_info'));
      }
      
      if (pdfs.room_service) {
        uploadPromises.push(pdfAPI.uploadPdf(pdfs.room_service, 'room_service'));
      }
      
      pdfs.additional_files.forEach(file => {
        uploadPromises.push(pdfAPI.uploadPdf(file, 'additional'));
      });

      if (uploadPromises.length > 0) {
        await Promise.all(uploadPromises);
      }

      onComplete(
        onboardRes.token && onboardRes.user && onboardRes.hotel
          ? {
              token: onboardRes.token,
              user: onboardRes.user,
              hotel: onboardRes.hotel,
            }
          : undefined
      );
    } catch (error: any) {
      setError(error.response?.data?.error || 'Setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-black-guestby">Hotel Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-black-guestby mb-2">
                  Hotel Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.hotel.name}
                  onChange={(e) => handleInputChange('hotel', 'name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  placeholder="Enter hotel name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black-guestby mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={formData.hotel.email}
                  onChange={(e) => handleInputChange('hotel', 'email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  placeholder="hotel@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black-guestby mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.hotel.phone}
                  onChange={(e) => handleInputChange('hotel', 'phone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  placeholder="+1 (555) 123-4567"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-black-guestby mb-2">
                  Address
                </label>
                <input
                  type="text"
                  value={formData.hotel.address}
                  onChange={(e) => handleInputChange('hotel', 'address', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  placeholder="123 Main St, City, State 12345"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-black-guestby mb-2">
                  Description
                </label>
                <textarea
                  value={formData.hotel.description}
                  onChange={(e) => handleInputChange('hotel', 'description', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  placeholder="Brief description of your hotel..."
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-black-guestby">Admin Account</h3>
            <p className="text-grey-guestby">Create the main administrator account for your hotel.</p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-black-guestby mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.admin.name}
                  onChange={(e) => handleInputChange('admin', 'name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  placeholder="Enter admin name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black-guestby mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  required
                  value={formData.admin.email}
                  onChange={(e) => handleInputChange('admin', 'email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  placeholder="admin@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-black-guestby mb-2">
                  Password *
                </label>
                <input
                  type="password"
                  required
                  value={formData.admin.password}
                  onChange={(e) => handleInputChange('admin', 'password', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  placeholder="Create a secure password"
                />
                <p className="text-xs text-grey-guestby mt-1">
                  Password should be at least 8 characters long
                </p>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-black-guestby">Upload Documents</h3>
            <p className="text-grey-guestby">
              Upload your hotel documents to train the AI concierge. All uploads are optional.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-black-guestby mb-2">
                  Hotel Information & Policies
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleFileChange('hotel_info', e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                />
                {pdfs.hotel_info && (
                  <p className="text-sm text-green-600 mt-1">✓ {pdfs.hotel_info.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-black-guestby mb-2">
                  Room Service Menu
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => handleFileChange('room_service', e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                />
                {pdfs.room_service && (
                  <p className="text-sm text-green-600 mt-1">✓ {pdfs.room_service.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-black-guestby mb-2">
                  Additional Documents (Spa Menu, Activities, etc.)
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    files.forEach(file => handleFileChange('additional_files', file));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                />
                {pdfs.additional_files.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {pdfs.additional_files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <span className="text-green-600">✓ {file.name}</span>
                        <button
                          onClick={() => removeAdditionalFile(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6 text-center">
            <div className="mx-auto h-16 w-16 bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-black-guestby">Ready to Complete Setup</h3>
            <p className="text-grey-guestby">
              Your hotel information and admin account will be created. 
              {(pdfs.hotel_info || pdfs.room_service || pdfs.additional_files.length > 0) && 
                ' Your documents will be processed to train the AI concierge.'
              }
            </p>
            
            <div className="bg-blue-50 rounded-lg p-4 text-left">
              <h4 className="font-medium text-black-guestby mb-2">Setup Summary:</h4>
              <ul className="text-sm text-grey-guestby space-y-1">
                <li>• Hotel: {formData.hotel.name}</li>
                <li>• Admin: {formData.admin.name} ({formData.admin.email})</li>
                <li>• Documents: {
                  [pdfs.hotel_info, pdfs.room_service, ...pdfs.additional_files]
                    .filter(Boolean).length
                } files uploaded</li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-black-guestby mb-2">Set Up Your Hotel</h1>
          <p className="text-grey-guestby">Get started with Hermes in just a few steps</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className={`flex items-center ${
                currentStep >= step.id ? 'text-blue-guestby' : 'text-gray-400'
              }`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
                  currentStep >= step.id 
                    ? 'bg-blue-guestby border-blue-guestby text-white' 
                    : 'border-gray-300'
                }`}>
                  <step.icon className="h-5 w-5" />
                </div>
                <span className="ml-2 text-sm font-medium hidden sm:block">{step.title}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`w-12 h-0.5 mx-4 ${
                  currentStep > step.id ? 'bg-blue-guestby' : 'bg-gray-300'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {renderStepContent()}

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <button
              onClick={currentStep === 1 ? onBack : handlePrevious}
              className="flex items-center px-4 py-2 text-grey-guestby hover:text-black-guestby"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {currentStep === 1 ? 'Back to Login' : 'Previous'}
            </button>

            {currentStep < 4 ? (
              <button
                onClick={handleNext}
                disabled={!validateStep(currentStep)}
                className="flex items-center px-6 py-2 bg-blue-guestby text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </button>
            ) : (
              <button
                onClick={handleComplete}
                disabled={loading}
                className="flex items-center px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Setting up...' : 'Complete Setup'}
                <CheckCircle className="h-4 w-4 ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
